import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { formatBRL, formatDateBR, formatPercent } from '../lib/format';
import KpiCard from '../components/KpiCard';
import PageHeader from '../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Coins, TrendingUp, Clock, Car, Receipt, Calendar, Filter, BarChart3, ArrowUpDown } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ComposedChart,
} from 'recharts';

const PARTNER_COLORS = ['#0f766e', '#f59e0b', '#6366f1', '#db2777', '#0ea5e9', '#84cc16'];
const MONTH_LABELS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const monthLabel = (m) => {
  const [y, mm] = (m || '').split('-');
  return mm ? `${MONTH_LABELS[Number(mm) - 1]}/${y.slice(2)}` : m;
};

export default function VehiclesReport() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    date_field: 'sale_date',
    partner_id: '',
  });
  const [sortBy, setSortBy] = useState('sale_date_desc');
  const [monthly, setMonthly] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      params.append('date_field', filters.date_field);
      if (filters.partner_id) params.append('partner_id', filters.partner_id);
      const { data: res } = await api.get(`/reports/vehicles?${params.toString()}`);
      setData(res);
    } catch (err) {
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { api.get('/partners').then((r) => setPartners(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get('/reports/monthly?months=6').then((r) => setMonthly(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);

  const sortedRows = useMemo(() => {
    if (!data) return [];
    const rows = [...data.rows];
    switch (sortBy) {
      case 'profit_desc': rows.sort((a, b) => b.net_profit_total - a.net_profit_total); break;
      case 'profit_asc': rows.sort((a, b) => a.net_profit_total - b.net_profit_total); break;
      case 'roi_desc': rows.sort((a, b) => b.roi_total_pct - a.roi_total_pct); break;
      case 'days_desc': rows.sort((a, b) => (b.days_in_stock || 0) - (a.days_in_stock || 0)); break;
      case 'days_asc': rows.sort((a, b) => (a.days_in_stock || 9999) - (b.days_in_stock || 9999)); break;
      case 'sale_date_desc':
      default: rows.sort((a, b) => (b.sale_date || '').localeCompare(a.sale_date || '')); break;
    }
    return rows;
  }, [data, sortBy]);

  const topProfitChart = useMemo(() => {
    if (!data) return [];
    return [...data.rows]
      .sort((a, b) => b.net_profit_total - a.net_profit_total)
      .slice(0, 10)
      .map((r) => ({ name: `${r.brand} ${r.model}`, lucro: r.net_profit_total, roi: r.roi_total_pct }));
  }, [data]);

  const monthlyChart = useMemo(() => {
    if (!monthly) return [];
    return monthly.months.map((m) => {
      const row = {
        label: monthLabel(m.month),
        total: m.profit_total,
        media: monthly.averages.profit,
        estoque: m.stock_count,
        vendas: m.sales_count,
        giro: m.sell_through_pct,
        media_estoque: monthly.averages.stock,
        media_vendas: monthly.averages.sales,
      };
      monthly.partners.forEach((p) => { row[p.name] = m.partners[p.id] || 0; });
      return row;
    });
  }, [monthly]);

  const k = data?.kpis || {};
  const clearFilters = () => setFilters({ date_from: '', date_to: '', date_field: 'sale_date', partner_id: '' });

  return (
    <>
      <PageHeader title="Relatório de Veículos" subtitle="Lucro, ROI e tempo em estoque por veículo vendido" />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <KpiCard label="Receita total" value={formatBRL(k.total_revenue || 0)} icon={Coins} intent="success" sublabel={`${k.sold_count || 0} veículo(s) vendido(s)`} testid="vr-kpi-revenue" />
        <KpiCard label="Lucro líquido" value={formatBRL(k.total_profit_total || 0)} icon={TrendingUp} intent={(k.total_profit_total || 0) >= 0 ? 'success' : 'danger'} sublabel="financeiro consolidado" testid="vr-kpi-profit-total" />
        <KpiCard label="ROI médio" value={formatPercent(k.avg_roi_total_pct || 0)} icon={BarChart3} intent={(k.avg_roi_total_pct || 0) >= 0 ? 'success' : 'danger'} sublabel="ponderado por investido" testid="vr-kpi-roi" />
        <KpiCard label="Total despesas" value={formatBRL(k.total_expenses || 0)} icon={Receipt} sublabel="nas vendas do período" testid="vr-kpi-expenses" />
      </div>
      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
        <KpiCard label="Tempo médio" value={`${k.avg_days_in_stock || 0}d`} icon={Clock} sublabel="compra → venda" testid="vr-kpi-days" />
        <KpiCard label="Vendidos" value={String(k.sold_count || 0)} icon={Car} intent="success" testid="vr-kpi-sold" />
        <KpiCard label="Em estoque" value={String(k.active_count || 0)} icon={Car} sublabel="ativos" testid="vr-kpi-active" />
      </div>

      {/* Charts */}
      {data && data.rows.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <Card className="border-border/70">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Lucro líquido por veículo (Top 10)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={topProfitChart} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${Math.round(v / 1000)}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                  <Tooltip formatter={(v) => formatBRL(v)} />
                  <Bar dataKey="lucro" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="border-border/70">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Lucro acumulado por data de venda</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.cumulative_chart}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v ? formatDateBR(v) : ''} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v) => formatBRL(v)} labelFormatter={(d) => formatDateBR(d)} />
                  <Line type="monotone" dataKey="cumulative" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Monthly charts (last 6 months) */}
      {monthly && monthlyChart.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <Card className="border-border/70" data-testid="vr-chart-monthly-profit">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Lucro por mês e por sócio (6 meses)</CardTitle>
              <div className="text-xs text-muted-foreground">
                Média mensal: <span className="font-medium text-foreground">{formatBRL(monthly.averages.profit)}</span>
                {monthly.partners.map((p, i) => (
                  <span key={p.id} className="ml-2" data-testid="vr-partner-average">
                    <span style={{ color: PARTNER_COLORS[i % PARTNER_COLORS.length] }}>●</span> {p.name}: {formatBRL(monthly.averages.per_partner?.[p.id] || 0)}
                  </span>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={monthlyChart} margin={{ left: 4, right: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v) => formatBRL(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {monthly.partners.map((p, i) => (
                    <Bar key={p.id} dataKey={p.name} fill={PARTNER_COLORS[i % PARTNER_COLORS.length]} radius={[3, 3, 0, 0]} />
                  ))}
                  <Line type="monotone" dataKey="media" name="Média mensal" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-border/70" data-testid="vr-chart-monthly-stock">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Estoque x Vendas por mês (6 meses)</CardTitle>
              <div className="text-xs text-muted-foreground">
                Médias: {monthly.averages.stock} carro(s) em estoque · {monthly.averages.sales} venda(s) · giro {monthly.averages.sell_through_pct}%
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={monthlyChart} margin={{ left: 4, right: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="qtd" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis yAxisId="pct" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v, name) => (name === 'Giro (vendas/estoque)' ? `${v}%` : v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="qtd" dataKey="estoque" name="Em estoque" fill="#64748b" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="qtd" dataKey="vendas" name="Vendas" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="pct" type="monotone" dataKey="giro" name="Giro (vendas/estoque)" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="qtd" type="monotone" dataKey="media_vendas" name="Média de vendas" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-border/70 mb-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Filter className="h-4 w-4" /> Filtros
            <button type="button" onClick={clearFilters} className="ml-auto text-xs text-muted-foreground hover:text-foreground underline" data-testid="vr-clear">Limpar</button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Filtrar por</Label>
              <Select value={filters.date_field} onValueChange={(v) => setFilters((f) => ({ ...f, date_field: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sale_date">Data de venda</SelectItem>
                  <SelectItem value="purchase_date">Data de compra</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">De</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="vr-filter-from" />
            </div>
            <div>
              <Label className="text-xs">Até</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="vr-filter-to" />
            </div>
            <div>
              <Label className="text-xs">Sócio</Label>
              <Select value={filters.partner_id || 'ALL'} onValueChange={(v) => setFilters((f) => ({ ...f, partner_id: v === 'ALL' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos os sócios</SelectItem>
                  {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ordenar por</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sale_date_desc">Data venda (mais recente)</SelectItem>
                  <SelectItem value="profit_desc">Lucro (maior)</SelectItem>
                  <SelectItem value="profit_asc">Lucro (menor)</SelectItem>
                  <SelectItem value="roi_desc">ROI % (maior)</SelectItem>
                  <SelectItem value="days_desc">Dias em estoque (maior)</SelectItem>
                  <SelectItem value="days_asc">Dias em estoque (menor)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border/70">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Veículos ({sortedRows.length})</CardTitle>
        </CardHeader>
        <CardContent className="px-3 md:px-6">
          {loading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Carregando...</div>
          ) : sortedRows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-lg">
              Nenhum veículo vendido nos filtros selecionados.
            </div>
          ) : (
            <>
              {/* Desktop */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-muted-foreground text-xs">
                    <tr>
                      <th className="text-left py-2 font-medium">Veículo</th>
                      <th className="text-center py-2 font-medium">Datas</th>
                      <th className="text-center py-2 font-medium">Compra</th>
                      <th className="text-center py-2 font-medium">Despesas</th>
                      <th className="text-center py-2 font-medium">Venda</th>
                      <th className="text-center py-2 font-medium">Impostos</th>
                      <th className="text-center py-2 font-medium">Lucro</th>
                      <th className="text-center py-2 font-medium">ROI %</th>
                      <th className="text-center py-2 font-medium">Dias</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/70">
                    {sortedRows.map((r) => (
                      <tr key={r.id} className="hover:bg-muted/30 cursor-pointer" onClick={() => window.open(`/veiculos/${r.id}?tab=dre`, '_blank', 'noopener')} data-testid="vr-row">
                        <td className="py-2.5 pr-3">
                          <div className="text-sm font-medium whitespace-nowrap">{r.brand} {r.model}</div>
                          <div className="text-[11px] text-muted-foreground whitespace-nowrap">{r.plate || '—'} {r.year ? `· ${r.year}` : ''}</div>
                        </td>
                        <td className="py-2.5 px-3 text-center text-xs text-muted-foreground whitespace-nowrap">
                          <div>📥 {formatDateBR(r.purchase_date)}</div>
                          <div>📤 {formatDateBR(r.sale_date)}</div>
                        </td>
                        <td className="py-2.5 px-3 text-center tabular text-muted-foreground whitespace-nowrap">{formatBRL(r.purchase_price)}</td>
                        <td className="py-2.5 px-3 text-center tabular text-muted-foreground whitespace-nowrap">{formatBRL(r.total_expenses)}</td>
                        <td className="py-2.5 px-3 text-center tabular font-medium whitespace-nowrap">{formatBRL(r.sale_price)}</td>
                        <td className="py-2.5 px-3 text-center tabular text-muted-foreground whitespace-nowrap">{formatBRL(r.total_taxes)}</td>
                        <td className="py-2.5 px-3 text-center whitespace-nowrap">
                          <TooltipProvider delayDuration={100}>
                            <UITooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className={`tabular font-semibold border-b border-dotted border-muted-foreground/50 ${r.net_profit_total >= 0 ? 'text-emerald-700' : 'text-red-700'}`}
                                  data-testid="vr-profit-value"
                                >
                                  {formatBRL(r.net_profit_total)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="left" data-testid="vr-profit-tooltip">
                                <div className="text-xs font-semibold mb-1">Lucro por sócio</div>
                                {(r.partner_profits || []).length === 0 ? (
                                  <div className="text-xs">Sem alocação de sócios</div>
                                ) : (
                                  <div className="space-y-0.5">
                                    {r.partner_profits.map((p) => (
                                      <div key={p.partner_id} className="flex justify-between gap-4 text-xs whitespace-nowrap">
                                        <span>{p.partner_name} · {formatPercent(p.profit_pct)}</span>
                                        <span className="tabular font-medium">{formatBRL(p.profit_share)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </TooltipContent>
                            </UITooltip>
                          </TooltipProvider>
                        </td>
                        <td className={`py-2.5 px-3 text-center tabular text-xs whitespace-nowrap ${r.roi_total_pct >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatPercent(r.roi_total_pct)}</td>
                        <td className="py-2.5 px-3 text-center tabular text-xs text-muted-foreground whitespace-nowrap">{r.days_in_stock !== null ? `${r.days_in_stock}d` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile + Tablet cards */}
              <div className="lg:hidden space-y-2">
                {sortedRows.map((r) => (
                  <div key={r.id} onClick={() => navigate(`/veiculos/${r.id}`)} className="border border-border/70 rounded-lg p-3 cursor-pointer hover:bg-muted/30" data-testid="vr-card">
                    <div className="flex items-start justify-between gap-2 mb-2 pb-2 border-b border-border/40">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{r.brand} {r.model}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{r.plate || '—'} {r.year ? `· ${r.year}` : ''}</div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                          📥 {formatDateBR(r.purchase_date)} → 📤 {formatDateBR(r.sale_date)}
                          {r.days_in_stock !== null && <span className="ml-1">({r.days_in_stock}d)</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`text-base font-bold tabular whitespace-nowrap ${r.net_profit_total >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatBRL(r.net_profit_total)}</div>
                        <div className={`text-xs font-medium ${r.roi_total_pct >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatPercent(r.roi_total_pct)}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                      <div className="flex justify-between"><span className="text-muted-foreground">Compra</span><span className="tabular">{formatBRL(r.purchase_price)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Venda</span><span className="tabular">{formatBRL(r.sale_price)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Despesas</span><span className="tabular">{formatBRL(r.total_expenses)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Impostos</span><span className="tabular">{formatBRL(r.total_taxes)}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}
